<template>
  <div>
    <v-app-bar
      flat
      color="transparent"
      class="main-appbar"
    >
      <app-bar-nav-icon />
      <div class="d-flex align-center">
        <v-toolbar-title>
          <h1 class="headline font-weight-bold">{{ pageTitle }}
          </h1>
        </v-toolbar-title>
      </div>
      <v-spacer />
      <div>
        <v-btn
          color="primary"
          class="mr-4"
          medium
          @click="onCancel"
          :disabled="form.$busy"
          text
          style="text-transform: none;"
        >Cancel</v-btn>
        <v-btn
          color="primary"
          medium
          @click="onSave"
          :loading="form.$busy"
          style="text-transform: none;"
        >Save</v-btn>
      </div>
    </v-app-bar>
    <v-row class="px-4 py-4">
      <v-col
        lg="8"
        md="6"
        sm="12"
        xs="12"
      >
        <v-card
          outlined
          class="px-6 py-8"
        >
          <v-text-field
            outlined
            flat
            label="Title"
            required
            v-model="form.title"
            :error-messages="form.$getError('title')"
          />
          <v-select
            :items="guide_type_options"
            v-model="form.type"
            label="Guide Type"
            :error-messages="form.$getError('type')"
          ></v-select>

          <v-switch
            label="Active"
            v-model="form.status"
            :error-messages="form.$getError('status')"
          ></v-switch>

          <image-file-input-viewer
            v-model="form.thumbnail"
            :error-messages="form.$getError('thumbnail')"
          />

          <content-editor
            v-model="form.content"
            :error-messages="form.$getError('content')"
          />
        </v-card>
      </v-col>
      <v-col
        lg="4"
        md="6"
        sm="12"
      >
        <slug-generator
          ref="slugGenerator"
          :base-string="form.title"
          v-model="slug"
          :error-messages="form.$getError('slug')"
        />

        <social-sharing-meta-tag
          v-bind:title.sync="form.social_summary_title"
          v-bind:description.sync="form.social_summary_description"
          v-bind:image.sync="form.social_thumbnail"
          class="mt-6"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import Guide from '@/models/Guide'
import Form from '@/utils/form'
import ContentEditor from '@/components/ContentEditor'
import SlugGenerator from '@/components/SlugGenerator'
import slugify from 'slugify'
import get from 'lodash/get'
import ImageFileInputViewer from '@/components/ImageFileInputViewer'
import { mapActions } from 'vuex'
import SocialSharingMetaTag from '@/components/SocialSharingMetaTag'

const initialData = {
  title: null,
  slug: null,
  content: null,
  type: null,
  status: false,
  thumbnail: null,
  social_summary_title: null,
  social_summary_description: null,
  social_thumbnail: null
}

export default {
  name: 'CreateGuide',
  data () {
    return {
      form: new Form({
        ...initialData
      }),
      guide_type_options: [
        {
          text: "Beginner's guide",
          value: 'BG'
        },
        {
          text: 'How To',
          value: 'HT'
        }
      ],
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      }

    }
  },

  components: {
    SlugGenerator,
    ContentEditor,
    AppBarNavIcon,
    ImageFileInputViewer,
    SocialSharingMetaTag
  },

  props: {
    guide: {
      type: [Guide, Object],
      default () {
        return {}
      }
    },
    pageTitle: {
      type: String,
      default: 'Create New Guide'
    },
    redirectCancelTo: {
      type: [String, Object],
      default () {
        return {
          name: 'user-guides'
        }
      }
    },
    redirectSaveTo: {
      type: [String, Object],
      default () {
        return {
          name: 'user-guides'
        }
      }
    }
  },

  methods: {
    ...mapActions({
      updateGuide: 'guide/updateGuide'
    }),

    onCancel () {
      this.$router.replace(this.redirectCancelTo)
    },

    async onSave () {
      try {
        this.form.$busy = true
        this.form.$clearErrors()
        const data = this.form.$data()
        data.slug = data.slug ? data.slug : this.slug

        if (this.isModeUpdate) {
          let formData = new FormData()
          formData.append('id', data.id)
          formData.append('status', data.status)
          formData.append('title', data.title)
          formData.append('content', data.content)
          formData.append('type', data.type)
          formData.append('thumbnail', data.thumbnail)
          formData.append('slug', data.slug)
          formData.append('_method', 'put')
          formData.append('social_summary_title', data.social_summary_title)
          formData.append('social_summary_description', data.social_summary_description)
          formData.append('social_thumbnail', data.social_thumbnail)
          await this.updateGuide(formData)
        } else {
          const guide = new Guide(data)
          await guide.save()
        }

        this.$router.replace(this.redirectSaveTo)
      } catch (error) {
        if (get(error, 'response.status') === 422) {
          if (get(error, 'response.data.errors') !== null) {
            this.form.$setErrors(get(error, 'response.data.errors'))
          } else {
            this.showSnackbar('Something went wrong.', 'error')
          }
        } else {
          this.showSnackbar(error, 'error')
        }
      } finally {
        this.form.$busy = false
      }
    },

    showSnackbar (message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }
  },

  computed: {
    slug: {
      get () {
        if (this.form.slug && this.form.slug.trim() !== '') {
          return this.form.slug
        }

        if (this.form.title) {
          return slugify(this.form.title, {
            remove: /[*+~.()'"!:@]/g,
            lower: true,
            strict: true
          })
        }

        return null
      },
      set (v) {
        this.form.slug = v
      }
    },
    isModeUpdate () {
      return get(this.form, 'id', null) !== null
    }
  },

  watch: {
    guide: {
      immediate: true,
      handler (val) {
        if (val) {
          this.form = new Form({
            ...initialData,
            ...val,
            social_thumbnail: val.socialThumbnail
          })

          if (!this.form.slug) {
            this.$nextTick(() => this.$refs['slugGenerator'].generate())
          }
        }
      }
    }
  }
}
</script>
